// Central definitions for ticket statuses, types and priorities.
// Colours are plain Tailwind utility classes so badges read well in light/dark.

// Board columns, in order. `key` matches the backend Ticket.Status values.
export const STATUSES = [
  { key: 'new', label: 'New', dot: 'bg-slate-400', header: 'text-slate-600 dark:text-slate-300' },
  { key: 'todo', label: 'To do', dot: 'bg-sky-500', header: 'text-sky-600 dark:text-sky-300' },
  { key: 'in_progress', label: 'In progress', dot: 'bg-amber-500', header: 'text-amber-600 dark:text-amber-300' },
  { key: 'blocked', label: 'Blocked', dot: 'bg-red-500', header: 'text-red-600 dark:text-red-300' },
  { key: 'validation', label: 'Validation', dot: 'bg-fuchsia-500', header: 'text-fuchsia-600 dark:text-fuchsia-300' },
  { key: 'done', label: 'Done', dot: 'bg-emerald-500', header: 'text-emerald-600 dark:text-emerald-300' },
];

export const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

export const TYPES = [
  { key: 'analysis', label: 'Analysis', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
  { key: 'coding', label: 'Coding', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  { key: 'bugfix', label: 'Bug fix', badge: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
  { key: 'feature', label: 'Feature', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  { key: 'question', label: 'Question', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  { key: 'other', label: 'Other', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300' },
];

export const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.key, t]));

// `weight` is used to sort by urgency (lower = more urgent).
export const PRIORITIES = [
  { key: 'urgent', label: 'Urgent', weight: 0, dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
  { key: 'high', label: 'High', weight: 1, dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' },
  { key: 'medium', label: 'Medium', weight: 2, dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  { key: 'low', label: 'Low', weight: 3, dot: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300' },
];

export const PRIORITY_MAP = Object.fromEntries(PRIORITIES.map((p) => [p.key, p]));

export const DEPARTMENTS = [
  { key: 'customer_support', label: 'Customer Support', badge: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
  { key: 'logistics', label: 'Logistics', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  { key: 'marketing', label: 'Marketing', badge: 'bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300' },
  { key: 'general', label: 'General', badge: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300' },
  { key: 'accounting', label: 'Accounting', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  { key: 'purchasing', label: 'Purchasing', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
];

export const DEPARTMENT_MAP = Object.fromEntries(DEPARTMENTS.map((d) => [d.key, d]));

// Recurrence cadences. Marking a recurring ticket done makes the backend create
// the next occurrence (same ticket, due date advanced) in To do. `short` is
// the compact text used next to the ↻ icon on cards and rows.
export const RECURRENCES = [
  { key: 'none', label: 'Does not repeat', short: '' },
  { key: 'daily', label: 'Every day', short: 'daily' },
  { key: 'weekly', label: 'Every week', short: 'weekly' },
  { key: 'biweekly', label: 'Every 2 weeks', short: '2-weekly' },
  { key: 'monthly', label: 'Every month', short: 'monthly' },
  { key: 'quarterly', label: 'Every quarter', short: 'quarterly' },
  { key: 'yearly', label: 'Every year', short: 'yearly' },
];

export const RECURRENCE_MAP = Object.fromEntries(RECURRENCES.map((r) => [r.key, r]));

export const isRecurring = (ticket) => Boolean(ticket?.recurrence && ticket.recurrence !== 'none');
